import { API, graphqlOperation } from "aws-amplify";
import { updateWorkOrder, updateWorkOrderItem } from "./custom-mutations-lite";

// Whitelist of scalar/list fields valid on UpdateWorkOrderInput (mirrors WorkOrder
// schema). Connection objects (account, area, zone, inputInventory, …) and
// inbound-only aliases produced by adaptWorkOrderForCrewDetail (lotNumber,
// unloadedBy, secondaryReview, sit, sealNumbers, checklist, …) are dropped
// here so callers can pass `currentShipment` directly without scrubbing fields
// in every UI dialog. Update this list when the WorkOrder schema changes.
const WORK_ORDER_INPUT_FIELDS = [
  "id",
  "owner",
  "groups",
  "active",
  "status",
  "state",
  "workType",
  "poNumber",
  "normalizedPoNumber",
  "totalUOM",
  "skipInvoice",
  "createDateTime",
  "startDateTime",
  "endDateTime",
  "attachments",
  "assignedTo",
  "workflowType",
  "processedBy",
  "verificationRequired",
  "hasException",
  "brokenSeal",
  "brokenSealNote",
  "otherException",
  "otherExceptionNote",
  "instructions",
  "notes",
  "history",
  "accountName",
  "accountId",
  "billToAccountId",
  "reloadYardId",
  "inputInventoryId",
  "invoiceId",
  "areaId",
  "zoneId",
  "reloadYardCommodityId",
  "customFields",
  "selectedCustomFields",
  "stepName",
];

const WORK_ORDER_ITEM_INPUT_FIELDS = [
  "id",
  "owner",
  "groups",
  "active",
  "status",
  "workOrderId",
  "sortKey",
  "poNumber",
  "quantityToProcess",
  "units",
  "history",
  "accountId",
  "reloadYardId",
  "inputInventoryId",
  "inputInventoryItemId",
];

// Drop GraphQL-managed metadata (e.g. __typename) from list items so payloads
// like `notes`/`history`/`selectedCustomFields` round-trip cleanly.
const stripTypename = (value) => {
  if (Array.isArray(value)) return value.map(stripTypename);
  if (value && typeof value === "object") {
    const out = {};
    for (const key of Object.keys(value)) {
      if (key === "__typename") continue;
      out[key] = stripTypename(value[key]);
    }
    return out;
  }
  return value;
};

const pickFields = (source = {}, fields) => {
  const out = {};
  for (const field of fields) {
    if (Object.prototype.hasOwnProperty.call(source, field)) {
      out[field] = stripTypename(source[field]);
    }
  }
  return out;
};

export const sanitizeWorkOrder = (workOrder = {}) =>
  pickFields(workOrder, WORK_ORDER_INPUT_FIELDS);

export const sanitizeWorkOrderItem = (workOrderItem = {}) =>
  pickFields(workOrderItem, WORK_ORDER_ITEM_INPUT_FIELDS);

export const performUpdateWorkOrder = async (workOrder) => {
  const workOrderInput = sanitizeWorkOrder(workOrder);
  if (!workOrderInput.id) {
    throw new Error("performUpdateWorkOrder: missing required id on input");
  }
  return API.graphql(graphqlOperation(updateWorkOrder, { input: workOrderInput }));
};

export const performUpdateWorkOrderItem = async (workOrderItem) => {
  const workOrderItemInput = sanitizeWorkOrderItem(workOrderItem);
  if (!workOrderItemInput.id) {
    throw new Error(
      "performUpdateWorkOrderItem: missing required id on input",
    );
  }
  return API.graphql(
    graphqlOperation(updateWorkOrderItem, { input: workOrderItemInput }),
  );
};
